import { useEffect, useRef, useState, useCallback } from "react";
import { HeatmapRow } from "./ThermalHeatMapHooks";
import { ThresholdConfig } from "../components/panels/ThermalAlertThresholds";
import useThermalHeatmap from "../../../../hooks/useThermalHeatmap";
import ApiService from "../../../../../../../service/ApiService";
import { getEnvVar } from "../../../../../../../envUtils";
import { THERMAL_HEATMAP_SUBSYSTEM_PLACEMENTS } from "../components/visualization/thermalHeatmapSubsystemConfig";
import { ThermalModelConfig } from "../../../../../../../../src/components/Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import { Thresholds, THRESHOLD_LEVELS } from "../../../../../../../../src/components/Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/SurfaceThresholdsTypes";
import {
  PANEL_TO_FACE_ID,
  ALL_FACE_LABEL_MAP,
  DESIGNER_PANEL_LABEL_TO_FACE_ID,
  FACE_ID_TO_DECK_MAP,
} from "../constants/thermalConstants";

export interface AlertEvent {
  key: string;
  face: string;
  faceLabel: string;
  thresholdId: string;
  thresholdName: string;
  severity: "warning" | "critical";
  breach: "high" | "low";
  /** Elapsed seconds from simulation start at breach start */
  timeSec: number;
  /** Elapsed seconds from simulation start at breach end */
  endTimeSec: number;
  /** Extreme temperature at the peak of the breach */
  peakTemp: number;
  /** Margin: deviation from the threshold value */
  margin: number;
  /** Optional: subsystem name if this alert is subsystem-level (not face/deck) */
  subsystemName?: string;
}

// ── API response types ────────────────────────────────────────────────────────

interface ApiAlertItem {
  thresholdLabel: string;
  panel: string;        // e.g. "-Y", "X"
  nodeIndex: number;
  breach: "high" | "low";
  startTimeEpochSec: number;
  endTimeEpochSec: number;
  extremeValueC: number;
  extremeTimeEpochSec: number;
  marginC: number;
  thresholdMinC: number;
  thresholdMaxC: number;
  severity: "warn" | "critical";
}


function mapApiAlerts(
  apiAlerts: ApiAlertItem[],
  thresholds: ThresholdConfig[],
  t0Epoch: number,
): AlertEvent[] {
  const result: AlertEvent[] = [];
  apiAlerts.forEach((a, idx) => {
    const faceId = PANEL_TO_FACE_ID[a.panel];
    if (!faceId) return;
    const threshold = thresholds.find((t) => t.name === a.thresholdLabel);
    const thresholdId =
      threshold?.id ?? a.thresholdLabel.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    result.push({
      key: `${a.panel}_${a.thresholdLabel}_N${a.nodeIndex}_${a.breach}_${a.startTimeEpochSec}_${idx}`,
      face: faceId,
      faceLabel: ALL_FACE_LABEL_MAP[faceId] ?? a.panel,
      thresholdId,
      thresholdName: a.thresholdLabel,
      severity: a.severity === "critical" ? "critical" : "warning",
      breach: a.breach,
      timeSec: a.startTimeEpochSec - t0Epoch,
      endTimeSec: a.endTimeEpochSec - t0Epoch,
      peakTemp: a.extremeValueC,
      margin: a.marginC,
    });
  });
  return result.sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === "critical" ? -1 : 1
  );
}

// ── Panel label mapping (Designer → Heatmap) ──────────────────────────────────
// Designer ThermalModelConfig uses panelLabel like "+X", "-X", "+Y", "-Y", "+Z", "-Z", "INT1", "INT2"
// Extends PANEL_TO_FACE_ID to handle Designer's "+" prefix format

// ── Subsystem threshold evaluation ──────────────────────────────────────────────

/**
 * Client-side subsystem threshold evaluation.
 * Evaluates each subsystem's temperature series against its configured thresholds.
 * Returns AlertEvent-shaped violations that can be merged with backend face/deck alerts.
 */
export function evaluateSubsystemThresholds(
  heatmapRows: HeatmapRow[],
  thermalModel: ThermalModelConfig | undefined | null,
  t0Epoch: number,
): AlertEvent[] {
  if (!thermalModel || !heatmapRows.length) return [];

  const { subsystemThresholds, panels } = thermalModel;
  if (!subsystemThresholds || Object.keys(subsystemThresholds).length === 0) return [];

  const subsystemAlerts: AlertEvent[] = [];

  // Walk each subsystem instance in the thermal model
  panels.forEach((panel) => {
    const faceId = DESIGNER_PANEL_LABEL_TO_FACE_ID[panel.panelLabel];
    if (!faceId) return; // Skip unrecognized panel labels

    panel.cells.forEach((cell) => {
      cell.subsystems.forEach((subsystem) => {
        // Look up thresholds for this subsystem by displayName
        const thresholds = subsystemThresholds[subsystem.displayName];
        if (!thresholds) return; // No thresholds configured for this subsystem, skip silently

        // Derive node key: FaceId + local cell index
        const nodeId = `${faceId}_N${cell.localIndex + 1}`;

        // Evaluate each threshold level (nonOperational, turnOn, etc.)
        THRESHOLD_LEVELS.forEach(({ key: thresholdKey, label: thresholdLabel }) => {
          const range = thresholds[thresholdKey as keyof Thresholds];
          if (!range || (range.min === null && range.max === null)) return; // Unconfigured level

          // High breach: temperature > max
          if (range.max !== null) {
            evaluateSubsystemBreach(
              subsystem.displayName,
              nodeId,
              thresholdKey,
              thresholdLabel,
              heatmapRows,
              range.max,
              "high",
              subsystemAlerts,
              t0Epoch,
              faceId,
            );
          }

          // Low breach: temperature < min
          if (range.min !== null) {
            evaluateSubsystemBreach(
              subsystem.displayName,
              nodeId,
              thresholdKey,
              thresholdLabel,
              heatmapRows,
              range.min,
              "low",
              subsystemAlerts,
              t0Epoch,
              faceId,
            );
          }
        });
      });
    });
  });

  // Sort by severity (critical first)
  return subsystemAlerts.sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === "critical" ? -1 : 1
  );
}

/**
 * Helper: Scan a subsystem's node temperature series for breach windows.
 * Emits one AlertEvent per contiguous breach period.
 */
function evaluateSubsystemBreach(
  subsystemName: string,
  nodeId: string,
  thresholdKey: string,
  thresholdLabel: string,
  heatmapRows: HeatmapRow[],
  thresholdValue: number,
  breach: "high" | "low",
  alerts: AlertEvent[],
  t0Epoch: number,
  faceId: string,
) {
  let breachStart: number | null = null;
  let breachPeakTemp = breach === "high" ? -Infinity : Infinity;
  let breachMaxMargin = 0;

  heatmapRows.forEach((row, rowIdx) => {
    const temp = row.nodes[nodeId] ?? NaN;
    if (Number.isNaN(temp)) return; // Skip missing data

    const isBreaching = breach === "high" ? temp > thresholdValue : temp < thresholdValue;
    const margin = breach === "high" ? temp - thresholdValue : thresholdValue - temp;

    if (isBreaching) {
      if (breachStart === null) breachStart = row.time_sec;
      if (breach === "high") {
        breachPeakTemp = Math.max(breachPeakTemp, temp);
      } else {
        breachPeakTemp = Math.min(breachPeakTemp, temp);
      }
      breachMaxMargin = Math.max(breachMaxMargin, margin);
    } else if (breachStart !== null) {
      // End of breach period: emit alert
      const endTime = rowIdx > 0 ? heatmapRows[rowIdx - 1].time_sec : row.time_sec;
      alerts.push({
        key: `subsys_${subsystemName}_${nodeId}_${thresholdKey}_${breach}_${breachStart}`,
        face: faceId,
        faceLabel: subsystemName,
        thresholdId: thresholdKey,
        thresholdName: thresholdLabel,
        severity: determineSubsystemSeverity(thresholdKey),
        breach,
        // heatmap row time_sec is already relative to simulation start
        // timeSec: breachStart - t0Epoch,
        // endTimeSec: endTime - t0Epoch,
        timeSec: breachStart,
        endTimeSec: endTime,
        peakTemp: breachPeakTemp,
        margin: Math.abs(breachMaxMargin),
        subsystemName,
      });
      breachStart = null;
      breachPeakTemp = breach === "high" ? -Infinity : Infinity;
      breachMaxMargin = 0;
    }
  });

  // Handle ongoing breach at end of simulation
  if (breachStart !== null && heatmapRows.length > 0) {
    const lastRow = heatmapRows[heatmapRows.length - 1];
    alerts.push({
      key: `subsys_${subsystemName}_${nodeId}_${thresholdKey}_${breach}_${breachStart}_ongoing`,
      face: faceId,
      faceLabel: subsystemName,
      thresholdId: thresholdKey,
      thresholdName: thresholdLabel,
      severity: determineSubsystemSeverity(thresholdKey),
      breach,
      // timeSec: breachStart - t0Epoch,
      // endTimeSec: lastRow.time_sec - t0Epoch,
      timeSec: breachStart,
      endTimeSec: lastRow.time_sec,
      peakTemp: breachPeakTemp,
      margin: Math.abs(breachMaxMargin),
      subsystemName,
    });
  }
}

/**
 * Helper: Map threshold level key to severity.
 * operatingLimits → critical, others → warning
 */
function determineSubsystemSeverity(thresholdKey: string): "warning" | "critical" {
  return thresholdKey === "operatingLimits" ? "critical" : "warning";
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useThermalAlerts(
  heatmapRows: HeatmapRow[],
  thresholds: ThresholdConfig[],
  nodesPerFace: number,
  t0Epoch: number | null,
  thermalModel?: ThermalModelConfig | null,
): { alerts: AlertEvent[]; isLoading: boolean; evaluate: (t: ThresholdConfig[]) => void } {
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { scenarioResponse, selectedGroupSatellite } = useThermalHeatmap();
  const didEvaluateRef = useRef(false);
  const thresholdsRef = useRef(thresholds);
  thresholdsRef.current = thresholds;

  const evaluate = useCallback(
    async (thresholdsToEval: ThresholdConfig[]) => {
      const missionId = scenarioResponse?.missionId;
      const scenarioId = scenarioResponse?.scenarioId;
      const satelliteGroupId = selectedGroupSatellite?.groupId;
      const satelliteId = selectedGroupSatellite?.satelliteId;
      if (!missionId || !scenarioId || !satelliteGroupId || !satelliteId) return;

      const enabledThresholds = thresholdsToEval.filter((t) => t.enabled);
      if (!enabledThresholds.length) {
        setAlerts([]);
        return;
      }

      const payload = {
        satelliteGroupId,
        satelliteId,
        thresholds: enabledThresholds.map((t) => ({
          label: t.name,
          minC: t.minC,
          maxC: t.maxC,
        })),
        includeTimeSeries: false,
      };

      setIsLoading(true);
      setAlerts([]);
      try {
        const { data } = await ApiService.post(
          `${getEnvVar("REACT_APP_BACKEND_BASE_URL")}test/${missionId}/scenario/${scenarioId}/prediction/thermalvisualizationreport/evaluate`,
          payload,
        );
        const apiAlerts: ApiAlertItem[] = data?.alerts ?? [];
        const faceAlerts = mapApiAlerts(apiAlerts, thresholdsToEval, t0Epoch ?? 0);

        // Merge with subsystem alerts (client-side evaluation)
        const subsystemAlerts = evaluateSubsystemThresholds(heatmapRows, thermalModel, t0Epoch ?? 0);

        // Combine and sort by severity
        const allAlerts = [...faceAlerts, ...subsystemAlerts].sort((a, b) =>
          a.severity === b.severity ? 0 : a.severity === "critical" ? -1 : 1
        );
        setAlerts(allAlerts);
      } catch {
        setAlerts([]);
      } finally {
        setIsLoading(false);
      }
    },
    [scenarioResponse, selectedGroupSatellite, t0Epoch, heatmapRows, thermalModel],
  );

  // Auto-evaluate when heatmap data first becomes available
  useEffect(() => {
    if (!heatmapRows.length || didEvaluateRef.current) return;
    didEvaluateRef.current = true;
    evaluate(thresholdsRef.current);
  }, [heatmapRows.length, evaluate]);

  // Reset state when heatmap data is cleared
  useEffect(() => {
    if (!heatmapRows.length) {
      didEvaluateRef.current = false;
      setAlerts([]);
      setIsLoading(false);
    }
  }, [heatmapRows.length]);

  return { alerts, isLoading, evaluate };
}

export interface SubsystemTempInfo {
  subsystemName: string;
  nodeId: string;
  nodeIndex: number;
  temperature: number;
  exceedance: number;
  isBreaching: boolean;
}

export function getSubsystemsForFace(faceId: string): Array<{ subsystemName: string; nodeId: string; nodeIndex: number }> {
  const deckKey = FACE_ID_TO_DECK_MAP[faceId];
  if (!deckKey) return [];

  return THERMAL_HEATMAP_SUBSYSTEM_PLACEMENTS
    .filter((placement) => placement.deck === deckKey)
    .map((placement) => {
      const nodeNum = parseInt(placement.nodeId.split("-")[1]) + 1;
      return {
        subsystemName: placement.label,
        nodeId: `${faceId}_N${nodeNum}`,
        nodeIndex: nodeNum,
      };
    });
}

export function getSubsystemTemperatures(
  faceId: string,
  heatmapRow: HeatmapRow | undefined,
  thresholdValue: number,
  breach: "high" | "low"
): SubsystemTempInfo[] {
  if (!heatmapRow) return [];

  const subsystems = getSubsystemsForFace(faceId);

  return subsystems.map((subsystem) => {
    const temperature = heatmapRow.nodes[subsystem.nodeId] ?? 0;
    const exceedance = breach === "high" 
      ? Math.max(0, temperature - thresholdValue)
      : Math.max(0, thresholdValue - temperature);
    const isBreaching = breach === "high" ? temperature > thresholdValue : temperature < thresholdValue;

    return {
      ...subsystem,
      temperature,
      exceedance,
      isBreaching,
    };
  });
}

export function formatSubsystemDescription(subsystemInfo: SubsystemTempInfo, breach: "high" | "low"): string {
  const breachVerb = breach === "high" ? "exceeded" : "fell below";
  const breachStatus = subsystemInfo.isBreaching ? breachVerb : "within threshold";

  if (subsystemInfo.isBreaching) {
    return `${subsystemInfo.subsystemName} on Node ${subsystemInfo.nodeIndex} ${breachVerb} threshold by ${subsystemInfo.exceedance.toFixed(1)}°C (${subsystemInfo.temperature.toFixed(1)}°C)`;
  } else {
    return `${subsystemInfo.subsystemName} on Node ${subsystemInfo.nodeIndex} is ${breachStatus} at ${subsystemInfo.temperature.toFixed(1)}°C`;
  }
}

export function getHeatmapRowForAlert(alert: AlertEvent, heatmapRows: HeatmapRow[]): HeatmapRow | undefined {
  if (!heatmapRows || heatmapRows.length === 0) return undefined;

  // Search for the row where any node on the face has temperature closest to peakTemp
  let bestRow: HeatmapRow | undefined;
  let bestDiff = Infinity;

  heatmapRows.forEach((row) => {
    // Get all node temperatures for this face
    const nodeTemps = Array.from({ length: 5 }, (_, i) =>
      row.nodes[`${alert.face}_N${i + 1}`] ?? null
    ).filter((t) => t !== null) as number[];

    if (nodeTemps.length === 0) return;

    // Find the node temperature closest to peakTemp
    nodeTemps.forEach((temp) => {
      const diff = Math.abs(temp - alert.peakTemp);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestRow = row;
      }
    });
  });

  return bestRow;
}

/**
 * True when the alert's deck has subsystems and at least one has breached the threshold.
 * Mirrors the check used by AlertDescriptionRow so click behaviour matches the description box.
 */
export function alertHasBreachingSubsystem(
  alert: AlertEvent,
  heatmapRows: HeatmapRow[],
  thresholds: ThresholdConfig[],
): boolean {
  if (getSubsystemsForFace(alert.face).length === 0) return false;
  // Subsystem-level alerts carry a threshold-level key, not a ThresholdConfig id; the alert itself proves the breach.
  if (alert.subsystemName) return true;
  return getSubsystemTemperatures(
    alert.face,
    getHeatmapRowForAlert(alert, heatmapRows),
    getThresholdValue(alert, thresholds),
    alert.breach,
  ).some((s) => s.isBreaching);
}

export function getThresholdValue(alert: AlertEvent, thresholds: ThresholdConfig[]): number {
  if (!thresholds) return 0;
  const threshold = thresholds.find((t) => t.id === alert.thresholdId);
  if (!threshold) return 0;
  return alert.breach === "high" ? (threshold.maxC ?? 0) : (threshold.minC ?? 0);
}

export function formatAlertSummary(alert: AlertEvent, thresholdValue: number): string {
  const breachVerb = alert.breach === "high" ? "exceeded" : "fell below";
  const thresholdLevelLabel = THRESHOLD_LEVELS.find((tl) => tl.key === alert.thresholdId)?.label ?? alert.thresholdId;

  return `${alert.faceLabel} ${breachVerb} the ${thresholdLevelLabel} threshold (${alert.thresholdName}) by ${alert.margin.toFixed(1)}°C, peaking at ${alert.peakTemp.toFixed(1)}°C between T+${Math.round(alert.timeSec)}s and T+${Math.round(alert.endTimeSec)}s`;
}

